const PRINT_CSS = `
  @page { size: A4; margin: 14mm 12mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { background: #fff; }
  body { font-family: "Segoe UI", Arial, Helvetica, sans-serif; font-size: 11.5px; line-height: 1.4; color: #111827; margin: 0; padding: 18px 22px; }
  .doc-head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #111827; padding-bottom: 8px; margin-bottom: 14px; }
  h1 { font-size: 19px; margin: 0; font-weight: 700; }
  .meta { color: #6b7280; font-size: 10.5px; text-align: right; }
  table { width: 100%; border-collapse: collapse; margin: 0 0 14px; }
  th, td { border-bottom: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; font-size: 10.5px; text-transform: uppercase; letter-spacing: .03em; color: #374151; border-bottom: 1px solid #9ca3af; }
  tbody tr:nth-child(even) td { background: #fafafa; }
  tfoot td { font-weight: 700; border-top: 2px solid #374151; }
  .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .neg { color: #b91c1c; }
  .pos { color: #15803d; }
  tr { page-break-inside: avoid; }
  .detail-row td { background: #f9fafb !important; padding: 8px 12px; }
  .report-cards, .finance-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 14px; }
  .report-card, .kpi-card { border: 1px solid #d1d5db; border-radius: 6px; padding: 7px 10px; background: #fff; page-break-inside: avoid; }
  .card-label, .kpi-label { color: #6b7280; font-size: 10px; text-transform: uppercase; letter-spacing: .03em; }
  .card-value, .kpi-value { font-weight: 700; font-size: 15px; margin-top: 2px; }
  .card-neg .card-value { color: #b91c1c; }
  .card-profit .card-value { color: #15803d; }
  .report-margin { margin: 0 0 12px; color: #374151; }
  .detail-grid { display: flex; gap: 28px; flex-wrap: wrap; }
  .detail-label, .section-title, .pl-section-title { font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #374151; margin: 0 0 5px; }
  .detail-line { padding: 1px 0; }
  .cell-sub { color: #6b7280; font-size: 10px; }
  .order-code { font-weight: 700; display: block; }
  .order-date { color: #6b7280; font-size: 10px; }
  .key-chip { font-family: Consolas, monospace; font-size: 10.5px; }
  .finance-layout, .wh-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: start; }
  .finance-right { display: flex; flex-direction: column; gap: 14px; }
  .pl-section, .wh-section { margin-bottom: 12px; page-break-inside: avoid; }
  .pl-row { display: flex; justify-content: space-between; gap: 12px; padding: 4px 0; border-bottom: 1px solid #eef0f3; }
  .pl-neg .pl-val { color: #b91c1c; }
  .pl-profit { border-top: 2px solid #111827; margin-top: 4px; font-weight: 700; }
  .pl-profit .pl-val { color: #15803d; }
  .pl-bold { font-weight: 700; }
  .pl-muted { color: #6b7280; }
  .pl-val { white-space: nowrap; font-variant-numeric: tabular-nums; }
  .expense-bar-row { display: grid; grid-template-columns: 1.2fr 2fr 40px 1fr; gap: 8px; align-items: center; padding: 3px 0; }
  .expense-bar-track, .bar-wrap { background: #e5e7eb; height: 7px; border-radius: 4px; overflow: hidden; }
  .expense-bar-fill, .bar-fill { height: 100%; }
  .expense-bar-pct, .expense-bar-amt { text-align: right; }
  .month-chart { display: flex; align-items: flex-end; gap: 8px; height: 120px; }
  .month-bar-col { flex: 1; text-align: center; font-size: 10px; }
  .month-bar-body { background: #6366f1; border-radius: 3px 3px 0 0; }
  .expand-btn, .no-print, button { display: none !important; }
`;

/**
 * Печать без новой вкладки: HTML кладётся в скрытый iframe и отправляется на печать.
 * На телефоне ничего не нужно закрывать — окно остаётся на странице CRM.
 */
export function printHtml(title: string, bodyHtml: string, subtitle = "") {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(frame);

  const doc = frame.contentWindow?.document;
  if (!doc || !frame.contentWindow) {
    frame.remove();
    alert("Не удалось подготовить печать");
    return;
  }
  doc.open();
  doc.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>${PRINT_CSS}</style></head><body>` +
      `<div class="doc-head"><h1>${title}</h1><div class="meta">${subtitle}<br>Напечатано: ${new Date().toLocaleString("ru-RU")}</div></div>` +
      `${bodyHtml}</body></html>`,
  );
  doc.close();

  const cleanup = () => setTimeout(() => frame.remove(), 500);
  frame.contentWindow.addEventListener("afterprint", cleanup);
  setTimeout(() => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } finally {
      // страховка, если afterprint не сработал
      setTimeout(() => frame.remove(), 120000);
    }
  }, 250);
}

export function escapeHtml(s: unknown): string {
  const map: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" };
  return String(s ?? "").replace(/[&<>"]/g, (c) => map[c]);
}
